import React, { useState } from 'react';
import { Star, Mail, User, MessageSquare, Send, Instagram } from 'lucide-react';
import { toast } from 'sonner';

const INK = 'var(--text-primary)';
const STEEL = 'var(--text-secondary)';
const RED = '#FF8618';
const INSTAGRAM_URL = 'https://www.instagram.com/gal_war_cc/?utm_source=ig_web_button_share_sheet';

const AboutContact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success('Message Sent', {
      description: "Thank you for reaching out. We'll get back to you soon.",
    });
    setFormData({ name: '', email: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-16 px-6 overflow-hidden"
    >
      {/* Soft warm blooms */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 55% 60% at 8% 50%, rgba(208,158,98,0.09) 0%, transparent 60%), radial-gradient(ellipse 70% 55% at 95% 80%, rgba(255,134,24,0.05) 0%, transparent 60%)',
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '26px 26px',
          maskImage: 'radial-gradient(ellipse 90% 70% at 50% 30%, black 0%, transparent 85%)',
          WebkitMaskImage: 'radial-gradient(ellipse 90% 70% at 50% 30%, black 0%, transparent 85%)',
        }}
      />

      <div className="relative z-10 max-w-6xl mx-auto">
        {/* HEADER */}
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-2.5 h-2.5" style={{ background: RED, transform: 'rotate(45deg)', boxShadow: '0 0 10px rgba(255,134,24,0.7)' }} />
            <span className="text-xs font-bold tracking-[0.55em] uppercase" style={{ fontFamily: '"Rajdhani", sans-serif', fontWeight: 700, color: RED }}>STUDIO</span>
            <div className="w-2.5 h-2.5" style={{ background: RED, transform: 'rotate(45deg)', boxShadow: '0 0 10px rgba(255,134,24,0.7)' }} />
          </div>

          {/* GAL WAR CC — dark metallic on light */}
          <h2
            className="font-black uppercase tracking-[0.04em] leading-[0.95]"
            style={{
              fontFamily: '"Russo One", "Orbitron", "Black Ops One", sans-serif',
              fontWeight: 900,
              fontSize: 'clamp(3rem, 9vw, 7rem)',
              background: 'linear-gradient(180deg, #f2f4f6 0%, #9B9FA3 55%, #C9CDD1 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', color: 'transparent',
              textShadow: '0 1px 0 rgba(255,255,255,0.5)',
            }}
            data-testid="studio-title"
          >
            GAL WAR CC
          </h2>

          <div className="flex flex-col items-center mt-5">
            <div className="w-3 h-3" style={{ background: RED, clipPath: 'polygon(50% 0, 100% 100%, 0 100%)', transform: 'rotate(180deg)', boxShadow: '0 0 12px rgba(255,134,24,0.7)' }} />
            <span className="block h-[2px] w-56 mt-1" style={{ background: 'linear-gradient(90deg, transparent 0%, #FF8618 50%, transparent 100%)', boxShadow: '0 0 14px rgba(255,134,24,0.5)' }} />
          </div>

          <p className="max-w-2xl mx-auto mt-7 text-sm md:text-base uppercase tracking-[0.32em]" style={{ fontFamily: '"Rajdhani", "Inter", sans-serif', fontWeight: 600, color: STEEL }}>
            Pioneering The Future Of Intergalactic Battle Royale Gaming.
          </p>
        </div>

        {/* TWO-COLUMN GRID */}
        <div className="grid md:grid-cols-2 gap-7">
          {/* LEFT — OUR VISION */}
          <BeveledPanel testid="about-vision-card">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 flex items-center justify-center" style={{ clipPath: 'none', borderRadius: '3px', background: 'rgba(255,134,24,0.1)', border: '1px solid rgba(255,122,0,0.45)', boxShadow: '0 0 12px rgba(255,134,24,0.2)' }}>
                <Star className="w-5 h-5" style={{ color: RED }} />
              </div>
              <div>
                <h3 className="text-2xl font-black uppercase tracking-[0.18em]" style={{ fontFamily: '"Russo One", "Rajdhani", sans-serif', fontWeight: 700, color: INK }}>Our Vision</h3>
                <span className="block mt-1.5 h-[2px] w-12" style={{ background: RED, boxShadow: '0 0 8px rgba(255,134,24,0.5)' }} />
              </div>
            </div>

            <div className="space-y-5 text-sm md:text-[15px] leading-relaxed" style={{ color: STEEL, fontFamily: '"Rajdhani", "Inter", sans-serif', fontWeight: 500 }}>
              <p>
                Gal War CC Pvt. Ltd. is dedicated to creating immersive, cutting-edge gaming experiences that push the boundaries of interactive entertainment.{' '}
                <span style={{ color: RED, fontWeight: 700 }}>Cosmic Conquest: Galactic War</span>{' '}
                represents our vision of the next generation of Battle Royale games.
              </p>
              <p>
                Our game combines innovative gameplay mechanics like the Terraform System with stunning visuals and deep strategic elements. We're committed to delivering a world-class gaming experience that challenges and thrills players.
              </p>
            </div>
          </BeveledPanel>

          {/* RIGHT — GET IN TOUCH */}
          <BeveledPanel testid="about-contact-card">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 flex items-center justify-center" style={{ clipPath: 'none', borderRadius: '3px', background: 'rgba(255,134,24,0.1)', border: '1px solid rgba(255,122,0,0.45)', boxShadow: '0 0 12px rgba(255,134,24,0.2)' }}>
                <Mail className="w-5 h-5" style={{ color: RED }} />
              </div>
              <div>
                <h3 className="text-2xl font-black uppercase tracking-[0.18em]" style={{ fontFamily: '"Russo One", "Rajdhani", sans-serif', fontWeight: 700, color: INK }}>Get In Touch</h3>
                <span className="block mt-1.5 h-[2px] w-12" style={{ background: RED, boxShadow: '0 0 8px rgba(255,134,24,0.5)' }} />
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
              <FormField icon={User} label="NAME">
                <input type="text" name="name" value={formData.name} onChange={handleChange} required data-testid="contact-name"
                  className="w-full bg-transparent outline-none text-sm" style={{ fontFamily: '"Rajdhani", "Inter", sans-serif', color: INK }} placeholder="Your name" />
              </FormField>

              <FormField icon={Mail} label="EMAIL">
                <input type="email" name="email" value={formData.email} onChange={handleChange} required data-testid="contact-email"
                  className="w-full bg-transparent outline-none text-sm" style={{ fontFamily: '"Rajdhani", "Inter", sans-serif', color: INK }} placeholder="you@galaxy.com" />
              </FormField>

              <FormField icon={MessageSquare} label="MESSAGE">
                <textarea name="message" value={formData.message} onChange={handleChange} required rows={5} data-testid="contact-message"
                  className="w-full bg-transparent outline-none text-sm resize-none" style={{ fontFamily: '"Rajdhani", "Inter", sans-serif', color: INK }} placeholder="Your message..." />
              </FormField>

              <button
                type="submit"
                data-testid="contact-submit"
                className="contact-submit group w-full relative flex items-center justify-center gap-3 px-6 py-4 mt-2"
                style={{
                  background: 'linear-gradient(180deg, #FF8618 0%, #F07310 100%)',
                  border: '1px solid #C6530D',
                  borderRadius: '4px',
                  clipPath: 'none',
                  boxShadow: '0 10px 26px rgba(255,134,24,0.30), inset 0 1px 0 rgba(255,255,255,0.25)',
                }}
              >
                <span className="text-sm font-black tracking-[0.32em] uppercase" style={{ fontFamily: '"Russo One", "Rajdhani", sans-serif', fontWeight: 700, color: '#FFFFFF' }}>Send Message</span>
                <Send className="w-4 h-4" style={{ color: '#FFFFFF' }} />
              </button>
            </form>

            {/* Follow / social */}
            <div className="mt-7 pt-6" style={{ borderTop: '1px solid rgba(255,255,255,0.11)' }}>
              <span className="block text-[10px] font-bold tracking-[0.34em] uppercase mb-3" style={{ fontFamily: '"Rajdhani", sans-serif', fontWeight: 700, color: 'var(--text-muted)' }}>
                Follow The War
              </span>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-instagram"
                className="instagram-link group inline-flex items-center gap-3 px-4 py-2.5 rounded-lg"
                style={{ background: 'rgba(20,27,34,0.7)', border: '1px solid rgba(255,134,24,0.4)', transition: 'border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease' }}
              >
                <Instagram className="w-4 h-4" style={{ color: RED }} />
                <span className="text-xs font-bold tracking-[0.22em] uppercase" style={{ fontFamily: '"Rajdhani", sans-serif', fontWeight: 700, color: 'var(--text-muted)' }}>
                  @gal_war_cc
                </span>
              </a>
            </div>
          </BeveledPanel>
        </div>
      </div>
    </section>
  );
};

/* Rectangular sci-fi panel */
const BeveledPanel = ({ children, testid }) => (
  <div
    data-testid={testid}
    className="relative p-7 lg:p-10"
    style={{
      background: 'rgba(16,22,28,0.72)',
      backdropFilter: 'blur(14px) saturate(140%)',
      WebkitBackdropFilter: 'blur(14px) saturate(140%)',
      border: '1px solid rgba(255,134,24,0.28)',
      boxShadow: '0 24px 60px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.05)',
      borderRadius: '6px',
    }}
  >
    {children}
  </div>
);

const FormField = ({ icon: Icon, label, children }) => (
  <div
    className="relative px-4 py-3.5 form-field rounded-lg"
    style={{ background: 'rgba(20,27,34,0.7)', border: '1px solid rgba(255,255,255,0.1)', transition: 'border-color 220ms ease, box-shadow 220ms ease' }}
  >
    <div className="flex items-center gap-2 mb-1.5">
      <Icon className="w-3 h-3" style={{ color: '#FF8618' }} />
      <span className="text-[10px] font-bold tracking-[0.3em] uppercase" style={{ fontFamily: '"Rajdhani", sans-serif', fontWeight: 700, color: 'var(--text-muted)' }}>{label}</span>
    </div>
    {children}
  </div>
);

export default AboutContact;
